import { useState, useEffect, useMemo } from 'react';
import { GpxRoute, RouteSeasonalWarning, Coordinates } from '../types';
import { fetchSnowDepth } from '../services/snowService';
import { fetchWinterClosedRoads } from '../services/nvdbService';
import { getDistance } from '../services/geometryUtils';

const PROXIMITY_M = 500; // Match within 500m of route

/** Check if a MM-DD date range covers today */
function isDateInRange(closedFrom?: string, closedTo?: string): boolean {
  if (!closedFrom || !closedTo) return false;
  const now = new Date();
  const mm = now.getMonth() + 1;
  const dd = now.getDate();
  const today = mm * 100 + dd; // e.g. 315 for March 15

  const [fM, fD] = closedFrom.split('-').map(Number);
  const [tM, tD] = closedTo.split('-').map(Number);
  if (!fM || !fD || !tM || !tD) return false;

  const from = fM * 100 + fD;
  const to = tM * 100 + tD;

  // Handle ranges that cross year boundary (e.g. 11-01 to 05-31)
  if (from <= to) return today >= from && today <= to;
  return today >= from || today <= to;
}

/** Format MM-DD to DD.MM for display */
function formatMMDD(mmdd?: string): string {
  if (!mmdd) return '';
  const [m, d] = mmdd.split('-');
  return `${d}.${m}`;
}

/** Sample key points along a route for snow checking */
function getSnowSamplePoints(route: GpxRoute): Coordinates[] {
  const points: Coordinates[] = [];

  // Start point
  if (route.startCoords) points.push(route.startCoords);

  // Highest elevation point (from elevation profile)
  if (route.elevationProfile && route.elevationProfile.length > 2) {
    const profile = route.elevationProfile;
    let maxIdx = 0;
    for (let i = 1; i < profile.length; i++) {
      if (profile[i].elevation > profile[maxIdx].elevation) maxIdx = i;
    }
    // Interpolate lat/lon from elevation profile fraction
    if (route.startCoords && route.endCoords && profile[profile.length - 1].distance > 0) {
      const frac = profile[maxIdx].distance / profile[profile.length - 1].distance;
      points.push({
        latitude: route.startCoords.latitude + frac * (route.endCoords.latitude - route.startCoords.latitude),
        longitude: route.startCoords.longitude + frac * (route.endCoords.longitude - route.startCoords.longitude),
      });
    }
  }

  // Midpoint
  if (route.center) points.push(route.center);

  // End point
  if (route.endCoords) points.push(route.endCoords);

  return points;
}

/** Get bounding box from route */
function getRouteBounds(route: GpxRoute) {
  const coords: Coordinates[] = [];
  if (route.startCoords) coords.push(route.startCoords);
  if (route.endCoords) coords.push(route.endCoords);
  if (route.center) coords.push(route.center);

  if (coords.length === 0) return null;

  const lats = coords.map(c => c.latitude);
  const lons = coords.map(c => c.longitude);
  const pad = 0.05; // ~5km padding

  return {
    latMin: Math.min(...lats) - pad,
    latMax: Math.max(...lats) + pad,
    lonMin: Math.min(...lons) - pad,
    lonMax: Math.max(...lons) + pad,
  };
}

export function useRouteSeasonalWarnings(route: GpxRoute | null) {
  const [snowWarnings, setSnowWarnings] = useState<RouteSeasonalWarning[]>([]);
  const [winterClosureWarnings, setWinterClosureWarnings] = useState<RouteSeasonalWarning[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Static warnings from AI assessment (stored on route)
  const staticWarnings = useMemo<RouteSeasonalWarning[]>(() => {
    if (!route) return [];
    const w: RouteSeasonalWarning[] = [];

    // High elevation warning
    if (route.elevationProfile && route.elevationProfile.length > 0) {
      const maxElev = Math.max(...route.elevationProfile.map(p => p.elevation));
      if (maxElev > 1000) {
        w.push({
          type: 'high_elevation',
          severity: maxElev > 1200 ? 'warning' : 'info',
          message: `Fjellovergang over ${Math.round(maxElev)}m`,
        });
      }
    }

    return w;
  }, [route?.id, route?.elevationProfile]);

  // Fetch real-time snow depth
  useEffect(() => {
    if (!route) { setSnowWarnings([]); return; }

    const samplePoints = getSnowSamplePoints(route);
    if (samplePoints.length === 0) { setSnowWarnings([]); return; }

    let cancelled = false;
    setIsLoading(true);

    (async () => {
      const warnings: RouteSeasonalWarning[] = [];
      try {
        const results = await Promise.allSettled(
          samplePoints.map(p => fetchSnowDepth(p.latitude, p.longitude))
        );

        let maxDepth = 0;
        for (const r of results) {
          if (r.status === 'fulfilled' && r.value) {
            maxDepth = Math.max(maxDepth, r.value.depthCm);
          }
        }

        if (!cancelled && maxDepth > 5) {
          warnings.push({
            type: 'snow',
            severity: maxDepth > 50 ? 'danger' : maxDepth > 20 ? 'warning' : 'info',
            message: maxDepth > 50
              ? `Mye snø (${Math.round(maxDepth)}cm) langs ruten`
              : `Snø (${Math.round(maxDepth)}cm) langs ruten`,
          });
        }
      } catch { /* ignore */ }

      if (!cancelled) {
        setSnowWarnings(warnings);
      }
    })();

    return () => { cancelled = true; };
  }, [route?.id]);

  // Check winter-closed roads near route
  useEffect(() => {
    if (!route) { setWinterClosureWarnings([]); return; }

    const bounds = getRouteBounds(route);
    if (!bounds) { setWinterClosureWarnings([]); return; }

    let cancelled = false;

    (async () => {
      try {
        const roads = await fetchWinterClosedRoads(bounds);
        if (cancelled) return;

        const routePoints: Coordinates[] = [];
        if (route.startCoords) routePoints.push(route.startCoords);
        if (route.center) routePoints.push(route.center);
        if (route.endCoords) routePoints.push(route.endCoords);

        const warnings: RouteSeasonalWarning[] = [];
        for (const road of roads) {
          // Check if any road geometry point is near any route point
          const isNear = road.geometry.some(rg =>
            routePoints.some(rp =>
              getDistance(rg.latitude, rg.longitude, rp.latitude, rp.longitude) < PROXIMITY_M
            )
          );

          if (isNear) {
            const isCurrentlyClosed = isDateInRange(road.closedFrom, road.closedTo);
            warnings.push({
              type: 'winter_closed',
              severity: isCurrentlyClosed ? 'danger' : 'info',
              message: isCurrentlyClosed
                ? `Vinterstengt vei (${formatMMDD(road.closedFrom)} – ${formatMMDD(road.closedTo)})`
                : `Vei vinterstengd ${formatMMDD(road.closedFrom)} – ${formatMMDD(road.closedTo)}`,
              location: road.description || road.name,
              closedFrom: road.closedFrom,
              closedTo: road.closedTo,
            });
          }
        }

        if (!cancelled) setWinterClosureWarnings(warnings);
      } catch {
        if (!cancelled) setWinterClosureWarnings([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [route?.id]);

  const warnings = useMemo(() => {
    // Deduplicate and sort: danger first, then warning, then info
    const all = [...winterClosureWarnings, ...snowWarnings, ...staticWarnings];
    const severityOrder = { danger: 0, warning: 1, info: 2 };
    return all.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);
  }, [snowWarnings, winterClosureWarnings, staticWarnings]);

  return { warnings, isLoading };
}
